import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllGradeMarks,
  getGradeMarkById,
  createGradeMark,
  updateGradeMark,
  deleteGradeMark
} from '../services/grademarkService';
import Notification from '../components/Common/Notification';

export const GradeMarkContext = createContext();

export const GradeMarkProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [gradeMarks, setGradeMarks] = useState([]);
  const [selectedGradeMark, setSelectedGradeMark] = useState(null);
  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllGradeMarks = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllGradeMarks(authHeaders);
      setGradeMarks(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching grade marks.';
      showNotification(errorMessage, true);
    }
  };

  const fetchGradeMarkById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getGradeMarkById(id, authHeaders);
      setSelectedGradeMark(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching grade mark with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addGradeMark = async (gradeMarkData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newGradeMark = await createGradeMark(gradeMarkData, authHeaders);
      setGradeMarks(prevGradeMarks => [...prevGradeMarks, newGradeMark]);
      showNotification('Grade mark added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding grade mark.';
      showNotification(errorMessage, true);
    }
  };

  const editGradeMark = async (id, gradeMarkData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedGradeMark = await updateGradeMark(id, gradeMarkData, authHeaders);
      setGradeMarks(prevGradeMarks =>
        prevGradeMarks.map(gm => (gm.id === id ? updatedGradeMark : gm))
      );
      showNotification('Grade mark updated successfully!', false);
      return updatedGradeMark;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating grade mark.';
      showNotification(errorMessage, true);
      throw error;
    }
  };

  const removeGradeMark = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteGradeMark(id, authHeaders);
      setGradeMarks(prevGradeMarks => prevGradeMarks.filter(gm => gm.id !== id));
      showNotification('Grade mark removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing grade mark.';
      showNotification(errorMessage, true);
    }
  };

  // useEffect(() => {
  //   fetchAllGradeMarks();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <GradeMarkContext.Provider
      value={{
        gradeMarks,
        selectedGradeMark,
        fetchAllGradeMarks,
        fetchGradeMarkById,
        addGradeMark,
        editGradeMark,
        removeGradeMark,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </GradeMarkContext.Provider>
  );
};
